import { useCallback, useEffect, useRef, useState } from "react";
import { idbResult, localTransaction } from "./operator-local-database";
import { operatorDraftDataSchema, operatorDraftScopeFromKey, operatorDraftSchema, recoverableOperatorDraft, type OperatorDraftData, type OperatorDraftRecord } from "./operator-drafts";
import { trackOperatorDraftWrite } from "./operator-draft-sync";

export function useStoredOperatorDraft(key: string, preview: boolean, initial: OperatorDraftData) {
  const [data, setData] = useState<OperatorDraftData>(initial);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const active = useRef<{ key: string; record: OperatorDraftRecord | null; pending: number; failed: boolean; tail: Promise<void>; live: boolean } | null>(null);
  useEffect(() => {
    const owner: NonNullable<typeof active.current> = { key, record: null, pending: 0, failed: false, tail: Promise.resolve(), live: true };
    active.current = owner; setData(initialRef.current); setLoading(true); setPending(0); setError(null);
    void localTransaction(preview, ["drafts"], "readwrite", async (tx) => {
      const store = tx.objectStore("drafts"), existing: unknown = await idbResult(store.get(key));
      if (existing !== undefined) return operatorDraftSchema.parse(existing);
      const target = operatorDraftScopeFromKey(key);
      const recovered = target ? recoverableOperatorDraft(await idbResult(store.getAll()), target) : null;
      // Recovery copies only a verified same-owner scope. A fresh intent id prevents an old request identity from being reused.
      const record = operatorDraftSchema.parse({ key, intentId: crypto.randomUUID(), revision: 1, data: recovered?.data ?? initialRef.current });
      await idbResult(store.add(record));
      if (recovered) await idbResult(store.delete(recovered.key));
      return record;
    }).then((record) => {
      owner.record = record;
      if (owner.live) { setData(record.data); setLoading(false); }
    }).catch((failure: unknown) => {
      owner.failed = true;
      if (owner.live) { setError(failure instanceof Error ? failure.message : "Cihaz taslağı okunamadı."); setLoading(false); }
    });
    const leaving = (event: BeforeUnloadEvent) => {
      if (owner.pending > 0 || owner.failed) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("beforeunload", leaving);
    return () => { owner.live = false; window.removeEventListener("beforeunload", leaving); };
  }, [key, preview, reloadVersion]);
  const change = useCallback((next: OperatorDraftData) => {
    const owner = active.current;
    if (!owner?.record || owner.key !== key) return;
    const validated = operatorDraftDataSchema.parse(next); // An allowlist, never arbitrary form state/PIN/token.
    setData(validated);
    if (owner.failed) return;
    owner.pending++; setPending(owner.pending);
    owner.tail = trackOperatorDraftWrite(owner.tail.then(async () => {
      const before = owner.record;
      if (!before) throw new Error("Taslak yüklenmeden değiştirilemez.");
      const after = await localTransaction(preview, ["drafts"], "readwrite", async (tx) => {
        const store = tx.objectStore("drafts"), raw: unknown = await idbResult(store.get(key));
        const saved = operatorDraftSchema.safeParse(raw);
        if (!saved.success || saved.data.intentId !== before.intentId || saved.data.revision !== before.revision) {
          throw new Error("Bu taslak başka bir pencerede değişti veya kuyruğa alındı. Alanlardaki değişiklik sunucuya gönderilmedi; cihazdaki taslağı yeniden yükleyin.");
        }
        const updated: OperatorDraftRecord = { ...before, revision: before.revision + 1, data: validated };
        await idbResult(store.put(updated)); return updated;
      });
      owner.record = after;
    }).catch((failure: unknown) => {
      owner.failed = true;
      if (owner.live) setError(failure instanceof Error ? failure.message : "Taslak cihazda saklanamadı.");
      throw failure;
    }).finally(() => { owner.pending--; if (owner.live) setPending(owner.pending); }));
    void owner.tail.catch(() => undefined);
  }, [key, preview]);
  const flush = useCallback(async () => {
    const owner = active.current;
    if (!owner || owner.key !== key || owner.failed) throw new Error("Cihaz taslağı doğrulanmadı; işlem gönderilmedi.");
    await owner.tail;
    if (!owner.record || owner.failed) throw new Error("Cihaz taslağı doğrulanmadı; işlem gönderilmedi.");
    return owner.record;
  }, [key]);
  const clear = useCallback(async () => {
    const record = await flush();
    await localTransaction(preview, ["drafts"], "readwrite", async (tx) => {
      const store = tx.objectStore("drafts"), raw: unknown = await idbResult(store.get(key));
      if (raw === undefined) return;
      const current = operatorDraftSchema.parse(raw);
      if (current.intentId === record.intentId && current.revision === record.revision) await idbResult(store.delete(key));
    });
  }, [flush, key, preview]);
  return { data, loading, pending: pending > 0, error, change, flush, clear,
    reload: () => setReloadVersion((value) => value + 1) };
}
